import { HoverOnlyTooltipComponent } from '@/components/Tooltip';
import { getStateOptions } from '@/helpers/getStateOptions';
import { PalmettoTextInput } from '@palmetto/dsm-components';
import { Box, Button, Icon, Modal, SelectInputNative } from '@palmetto/palmetto-components';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { OrganizationUnderwritingSettingsV2, ProgramType, ProgramTypeLabels } from 'types';

import {
  CREDIT_OPTIMIZATION_TOOLTIP,
  MULTI_APPLICANT_MIN_FICO_TOOLTIP,
  noop,
  tooltipContainerStyle,
  tooltipContentStyle,
  tooltipTriggerStyle,
  validateCoApplicantMinFico,
  validateCreditOptimizationRange,
} from './constants';

interface FormValues {
  minFicoScore: number;
  ficoScoreErrorThreshold: number;
  minFicoToRerun: number;
  yearsSinceLastBankruptcy: number;
  maxAppsPerDay: number;
  autoQualify: boolean;
  // Optional: blank means "no multiple-applicant override". Held as a string while editing.
  multipleApplicantMinimumFico?: number | string;
  // Optional: blank means optimization is off. Held as a string while editing.
  creditOptimizationMin?: number | string;
  creditOptimizationMax?: number | string;
}

export interface UnderwritingSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: OrganizationUnderwritingSettingsV2, effectiveDate: string) => Promise<void>;
  currentSettings?: OrganizationUnderwritingSettingsV2;
  sabalReference?: OrganizationUnderwritingSettingsV2;
  programType: ProgramType;
  isCreate: boolean;
  isStateOverride?: boolean;
  stateCode?: string;
}

const FIELD_CONFIG: {
  key: keyof Omit<FormValues, 'autoQualify'>;
  label: string;
}[] = [
  { key: 'minFicoScore', label: 'Min FICO Score' },
  { key: 'ficoScoreErrorThreshold', label: 'FICO Error Threshold' },
  { key: 'minFicoToRerun', label: 'Min FICO to Rerun' },
  { key: 'yearsSinceLastBankruptcy', label: 'Years Since Bankruptcy' },
  { key: 'maxAppsPerDay', label: 'Max Apps / Day' },
];

// Columns collapse rather than squeeze: below ~320px per column the DSM inputs' inset
// labels (e.g. "Min FICO Score (Co-Applicant)") wrap onto the value line.
const GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '16px',
};

const NUMERIC_RULES = { required: 'Required', min: { value: 0, message: 'Must be >= 0' } } as const;

const fieldRelativeStyle = { position: 'relative' } as const;
// Overlay the info icon on the field's label line (top-right) without affecting input layout.
const tooltipOverlayStyle = { position: 'absolute', top: '7px', right: '8px', zIndex: 1, lineHeight: 1 } as const;

const AUTO_QUALIFY_OPTIONS = [
  { label: 'No', value: 'false' },
  { label: 'Yes', value: 'true' },
];

export function UnderwritingSettingsModal({
  isOpen,
  onClose,
  onSubmit,
  currentSettings,
  sabalReference,
  programType,
  isCreate,
  isStateOverride = false,
  stateCode,
}: UnderwritingSettingsModalProps) {
  const defaults = isCreate && !stateCode ? sabalReference : currentSettings;

  const [selectedState, setSelectedState] = useState<string>(stateCode ?? '');
  const [stateError, setStateError] = useState<string>('');

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    trigger,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: {
      minFicoScore: defaults?.minFicoScore ?? 0,
      ficoScoreErrorThreshold: defaults?.ficoScoreErrorThreshold ?? 0,
      minFicoToRerun: defaults?.minFicoToRerun ?? 0,
      yearsSinceLastBankruptcy: defaults?.yearsSinceLastBankruptcy ?? 0,
      maxAppsPerDay: defaults?.maxAppsPerDay ?? 0,
      autoQualify: defaults?.autoQualify ?? false,
      multipleApplicantMinimumFico: defaults?.multipleApplicantMinimumFico ?? '',
      creditOptimizationMin: defaults?.creditOptimizationMin ?? '',
      creditOptimizationMax: defaults?.creditOptimizationMax ?? '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      const src = isCreate && !stateCode ? sabalReference : currentSettings;
      reset({
        minFicoScore: src?.minFicoScore ?? 0,
        ficoScoreErrorThreshold: src?.ficoScoreErrorThreshold ?? 0,
        minFicoToRerun: src?.minFicoToRerun ?? 0,
        yearsSinceLastBankruptcy: src?.yearsSinceLastBankruptcy ?? 0,
        maxAppsPerDay: src?.maxAppsPerDay ?? 0,
        autoQualify: src?.autoQualify ?? false,
        multipleApplicantMinimumFico: src?.multipleApplicantMinimumFico ?? '',
        creditOptimizationMin: src?.creditOptimizationMin ?? '',
        creditOptimizationMax: src?.creditOptimizationMax ?? '',
      });
      setSelectedState(stateCode ?? '');
      setStateError('');
    }
  }, [isOpen, isCreate, currentSettings, sabalReference, reset, stateCode]);

  const submit = useCallback(
    async (data: FormValues) => {
      if (isStateOverride && isCreate && !selectedState) {
        setStateError('Required');
        return;
      }
      const {
        autoQualify,
        multipleApplicantMinimumFico,
        creditOptimizationMin,
        creditOptimizationMax,
        ...numericFields
      } = data;
      // Number inputs deliver e.target.value as strings via onChange, so coerce every
      // numeric field back to a number — otherwise edited fields reach the API/Mongo as
      // strings and break FICO comparisons.
      const value: OrganizationUnderwritingSettingsV2 = {
        minFicoScore: Number(numericFields.minFicoScore),
        ficoScoreErrorThreshold: Number(numericFields.ficoScoreErrorThreshold),
        minFicoToRerun: Number(numericFields.minFicoToRerun),
        creditExpiryTerm: 180,
        yearsSinceLastBankruptcy: Number(numericFields.yearsSinceLastBankruptcy),
        maxAppsPerDay: Number(numericFields.maxAppsPerDay),
        autoQualify,
      };
      const multiApplicantMin = String(multipleApplicantMinimumFico ?? '').trim();
      if (multiApplicantMin !== '') {
        value.multipleApplicantMinimumFico = Number(multiApplicantMin);
      }
      const optMin = String(creditOptimizationMin ?? '').trim();
      const optMax = String(creditOptimizationMax ?? '').trim();
      if (optMin !== '') value.creditOptimizationMin = Number(optMin);
      if (optMax !== '') value.creditOptimizationMax = Number(optMax);
      if (isStateOverride) {
        value.state = selectedState || stateCode;
      }
      await onSubmit(value, new Date().toISOString());
    },
    [onSubmit, isStateOverride, isCreate, selectedState, stateCode],
  );

  const handleFormSubmit = useCallback(() => void handleSubmit(submit)(), [handleSubmit, submit]);

  // Validate against the live Min FICO Score field (must be ≥ standard, 300–850, whole number).
  const coApplicantFicoRules = useMemo(
    () => ({
      validate: (value: number | string | undefined) => validateCoApplicantMinFico(value, getValues('minFicoScore')),
    }),
    [getValues],
  );

  // Each bound is optional; when set it must be a whole number 300–850, and Max must be ≥ Min.
  const creditOptimizationMinRules = useMemo(
    () => ({
      validate: (value: number | string | undefined) =>
        validateCreditOptimizationRange(value, getValues('creditOptimizationMax'), false),
    }),
    [getValues],
  );
  const creditOptimizationMaxRules = useMemo(
    () => ({
      validate: (value: number | string | undefined) =>
        validateCreditOptimizationRange(value, getValues('creditOptimizationMin'), true),
    }),
    [getValues],
  );

  // RHF doesn't re-validate the sibling bound when one changes, so the both-or-neither rule can
  // go stale on the untouched field. Re-trigger the sibling after each bound edits.
  const revalidateOptimizationMax = useCallback(() => void trigger('creditOptimizationMax'), [trigger]);
  const revalidateOptimizationMin = useCallback(() => void trigger('creditOptimizationMin'), [trigger]);

  const title = isStateOverride
    ? `${isCreate ? 'Create' : 'Edit'} ${ProgramTypeLabels[programType]} State Override`
    : `${isCreate ? 'Create' : 'Edit'} ${ProgramTypeLabels[programType]} Underwriting Settings`;

  const stateSelectOptions = [{ label: 'Select a state...', value: '' }, ...getStateOptions()];

  const handleStateChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedState(e.target.value);
    setStateError('');
  }, []);

  return (
    <Modal isOpen={isOpen} onDismiss={onClose} maxWidth="4xl" ariaLabelledBy="underwriting-settings-modal">
      <Modal.Header id="underwriting-settings-modal" title={title} onDismiss={onClose} />
      <Modal.Body background="secondary" childGap="lg" padding="lg">
        {isCreate && (
          <Box
            background="info-200"
            borderWidth="xs"
            borderColor="info"
            padding="sm md"
            fontSize="sm"
            color="info-600"
          >
            {isStateOverride && stateCode
              ? 'Fields are pre-filled with the inherited settings for this state.'
              : isStateOverride
                ? "Fields are pre-filled with this organization's baseline settings."
                : "No settings exist for this program type. Fields are pre-filled with Sabal's values as a starting point."}
          </Box>
        )}

        {isStateOverride && isCreate && !stateCode && (
          <Box childGap="xs" style={{ maxWidth: 'calc(50% - 8px)' }}>
            <SelectInputNative
              id="state-override-state"
              label="State"
              value={selectedState}
              onChange={handleStateChange}
              options={stateSelectOptions}
              error={stateError || undefined}
            />
          </Box>
        )}

        {isStateOverride && (!isCreate || stateCode) && (
          <Box fontWeight="bold">State: {stateCode || selectedState}</Box>
        )}

        <Box childGap="lg">
          <div style={GRID_STYLE}>
            {FIELD_CONFIG.map(({ key, label }) => {
              const fieldBox = (
                <Box key={key} childGap="xs">
                  <Controller
                    control={control}
                    name={key}
                    rules={NUMERIC_RULES}
                    render={({ field }) => (
                      <PalmettoTextInput
                        {...field}
                        value={String(field.value)}
                        onChange={(e) => field.onChange(e.target.value)}
                        type="number"
                        label={label}
                        validationMessage={
                          errors[key] ? { type: 'error' as const, text: errors[key].message! } : undefined
                        }
                      />
                    )}
                  />
                  {sabalReference && !isStateOverride && (
                    <Box fontSize="xs" color="grey-500" margin="xs 0 0 0">
                      Sabal: {sabalReference[key] ?? '—'}
                    </Box>
                  )}
                </Box>
              );

              // Keep Multi-Applicant Min FICO next to Min FICO Score, matching the display card.
              if (key !== 'minFicoScore') {
                return fieldBox;
              }

              return (
                <Fragment key={key}>
                  {fieldBox}
                  <Box childGap="xs" style={fieldRelativeStyle}>
                    <Controller
                      control={control}
                      name="multipleApplicantMinimumFico"
                      rules={coApplicantFicoRules}
                      render={({ field }) => (
                        <PalmettoTextInput
                          {...field}
                          value={field.value == null ? '' : String(field.value)}
                          onChange={(e) => field.onChange(e.target.value)}
                          type="number"
                          label="Min FICO Score (Co-Applicant)"
                          validationMessage={
                            errors.multipleApplicantMinimumFico
                              ? { type: 'error' as const, text: errors.multipleApplicantMinimumFico.message! }
                              : undefined
                          }
                        />
                      )}
                    />
                    <Box style={tooltipOverlayStyle}>
                      <HoverOnlyTooltipComponent
                        content={MULTI_APPLICANT_MIN_FICO_TOOLTIP}
                        onClose={noop}
                        displayedContainerStyle={tooltipTriggerStyle}
                        contentStyle={tooltipContentStyle}
                        tooltipContainerStyle={tooltipContainerStyle}
                        withPortal
                      >
                        <Icon name="c-info" size="md" color="grey-500" />
                      </HoverOnlyTooltipComponent>
                    </Box>
                    {sabalReference && !isStateOverride && (
                      <Box fontSize="xs" color="grey-500" margin="xs 0 0 0">
                        Sabal: {sabalReference.multipleApplicantMinimumFico ?? '—'}
                      </Box>
                    )}
                  </Box>
                </Fragment>
              );
            })}
          </div>

          <div style={GRID_STYLE}>
            <Box childGap="xs" style={fieldRelativeStyle}>
              <Controller
                control={control}
                name="creditOptimizationMin"
                rules={creditOptimizationMinRules}
                render={({ field }) => (
                  <PalmettoTextInput
                    {...field}
                    value={String(field.value ?? '')}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      revalidateOptimizationMax();
                    }}
                    type="number"
                    label="Credit Optimization Min"
                    validationMessage={
                      errors.creditOptimizationMin
                        ? { type: 'error' as const, text: errors.creditOptimizationMin.message! }
                        : undefined
                    }
                  />
                )}
              />
              <Box style={tooltipOverlayStyle}>
                <HoverOnlyTooltipComponent
                  content={CREDIT_OPTIMIZATION_TOOLTIP}
                  onClose={noop}
                  displayedContainerStyle={tooltipTriggerStyle}
                  contentStyle={tooltipContentStyle}
                  tooltipContainerStyle={tooltipContainerStyle}
                  withPortal
                >
                  <Icon name="c-info" size="md" color="grey-500" />
                </HoverOnlyTooltipComponent>
              </Box>
              {sabalReference && !isStateOverride && (
                <Box fontSize="xs" color="grey-500" margin="xs 0 0 0">
                  Sabal: {sabalReference.creditOptimizationMin ?? '—'}
                </Box>
              )}
            </Box>
            <Box childGap="xs">
              <Controller
                control={control}
                name="creditOptimizationMax"
                rules={creditOptimizationMaxRules}
                render={({ field }) => (
                  <PalmettoTextInput
                    {...field}
                    value={String(field.value ?? '')}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      revalidateOptimizationMin();
                    }}
                    type="number"
                    label="Credit Optimization Max"
                    validationMessage={
                      errors.creditOptimizationMax
                        ? { type: 'error' as const, text: errors.creditOptimizationMax.message! }
                        : undefined
                    }
                  />
                )}
              />
              {sabalReference && !isStateOverride && (
                <Box fontSize="xs" color="grey-500" margin="xs 0 0 0">
                  Sabal: {sabalReference.creditOptimizationMax ?? '—'}
                </Box>
              )}
            </Box>
          </div>

          <Box childGap="xs" style={{ maxWidth: 'calc(50% - 8px)' }}>
            <Controller
              control={control}
              name="autoQualify"
              render={({ field }) => (
                <SelectInputNative
                  id="autoQualify"
                  label="Auto Qualify"
                  value={String(field.value)}
                  onChange={(e) => field.onChange(e.target.value === 'true')}
                  options={AUTO_QUALIFY_OPTIONS}
                />
              )}
            />
            {sabalReference && !isStateOverride && (
              <Box fontSize="xs" color="grey-500" margin="xs 0 0 0">
                Sabal: {sabalReference.autoQualify ? 'Yes' : 'No'}
              </Box>
            )}
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" tone="neutral" isDisabled={isSubmitting} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" isLoading={isSubmitting} isDisabled={isSubmitting} onClick={handleFormSubmit}>
          {isCreate ? 'Create' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
